import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const hostname = window && window.location && window.location.hostname;

const apiCall = async (endpoint, options = {}) => {
  const backendUrl = hostname === "evchargedata.com" ? "https://www.evchargedata.com" : "http://localhost:8080";
  console.log(hostname);
  console.log(backendUrl);
  const url = `${backendUrl}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  return fetch(url, config);
};

// Fix for default markers in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom charging station icons
const createChargingIcon = (maxPower, isSelected = false) => {
  let color = '#10b981';
  let size = 'M';

  if (maxPower) {
    if (maxPower >= 150) {
      color = '#8b5cf6';
      size = 'XL';
    } else if (maxPower >= 50) {
      color = '#f59e0b';
      size = 'L';
    } else if (maxPower >= 22) {
      color = '#3b82f6';
      size = 'M';
    }
  }

  const sizeMap = { S: 20, M: 25, L: 30, XL: 35 };
  const iconSize = sizeMap[size];

  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="${iconSize}" height="${iconSize}">
      <circle cx="12" cy="12" r="11" fill="white" stroke="${color}" stroke-width="2"/>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="${color}"/>
      ${isSelected ? '<circle cx="12" cy="12" r="11" fill="none" stroke="#ff0000" stroke-width="3"/>' : ''}
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize / 2],
    popupAnchor: [0, -iconSize / 2],
    className: 'charging-station-marker'
  });
};

const userLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <circle cx="12" cy="12" r="8" fill="#3b82f6"/>
      <circle cx="12" cy="12" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Map Controller Component
const MapController = ({ center, stations, onStationClick, selectedStationId, userLocation, searchRadius }) => {
  const map = useMap();
  const previousCenter = useRef(center);
  const searchCircleRef = useRef(null);

  useEffect(() => {
    if (center && (
      !previousCenter.current ||
      Math.abs(center[0] - previousCenter.current[0]) > 0.001 ||
      Math.abs(center[1] - previousCenter.current[1]) > 0.001
    )) {
      map.setView(center, map.getZoom());
      previousCenter.current = center;
    }
  }, [center, map]);

  useEffect(() => {
    if (stations.length > 0) {
      const bounds = L.latLngBounds(stations.map(station => [station.lat, station.lng]));
      map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 });
    }
  }, [stations, map]);

  useEffect(() => {
    if (searchCircleRef.current) {
      map.removeLayer(searchCircleRef.current);
    }

    if (userLocation && searchRadius) {
      searchCircleRef.current = L.circle([userLocation.lat, userLocation.lng], {
        radius: searchRadius * 1000,
        fillColor: '#3b82f6',
        fillOpacity: 0.1,
        color: '#3b82f6',
        weight: 2,
        dashArray: '5, 5'
      }).addTo(map);
    }

    return () => {
      if (searchCircleRef.current) {
        map.removeLayer(searchCircleRef.current);
      }
    };
  }, [userLocation, searchRadius, map]);

  return null;
};

// Station Popup Component
const StationPopup = ({ station, onViewDetails, onDirections }) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  const formatPower = (power) => {
    if (!power) return 'Unknown';
    if (power >= 1000) return `${(power / 1000).toFixed(1)}MW`;
    return `${power}kW`;
  };

  return (
    <div className="min-w-64 p-3">
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-gray-900 text-sm leading-tight">
          {station.name || 'Unnamed Station'}
        </h3>
        {station.max_power && (
          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded ml-2">
            {formatPower(station.max_power)}
          </span>
        )}
      </div>

      <div className="space-y-2 text-sm">
        {station.average_rating > 0 && (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              {renderStars(station.average_rating)}
            </div>
            <span className="text-gray-600 text-xs">
              ({station.average_rating.toFixed(1)})
            </span>
          </div>
        )}

        <p className="text-gray-600 text-xs">
          📍 {station.full_address || `${station.city}, ${station.country}`}
        </p>

        {station.network && (
          <p className="text-blue-600 font-medium text-xs">🔌 {station.network}</p>
        )}

        {station.distance_km && (
          <p className="text-gray-500 text-xs">
            📏 {station.distance_km.toFixed(1)} km away
          </p>
        )}
      </div>

      <div className="pt-3 border-t border-gray-200 mt-3 space-y-2">
        <button
          onClick={() => onViewDetails(station)}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-sm font-medium"
        >
          View Details
        </button>

        <button
          onClick={() => onDirections(station)}
          className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded hover:bg-gray-200 text-sm font-medium"
        >
          🗺️ Get Directions
        </button>
      </div>
    </div>
  );
};

// Updated MapView Component with three view modes
const StationsView = ({ selectedStation, setSelectedStation, setCurrentView }) => {
  const [stations, setStations] = useState([]);
  const [searchLocation, setSearchLocation] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  // Updated view mode state - now includes 'map'
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'list', or 'map'
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalStations, setTotalStations] = useState(0);

  // Map-specific state
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
  const [searchRadius, setSearchRadius] = useState(10);
  const [selectedStationId, setSelectedStationId] = useState(null);

  useEffect(() => {
    loadStations();
    getUserLocation();
  }, []);

  useEffect(() => {
    if (stations.length > 0) {
      loadStations();
    }
  }, [sortBy, sortOrder, currentPage, itemsPerPage]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          setMapCenter([location.lat, location.lng]);
        },
        (error) => console.log('Location access denied')
      );
    }
  };

  const loadStations = async (lat, lng, radius = 10, resetPage = false) => {
    setLoading(true);
    try {
      let url = '/api/stations';
      const params = new URLSearchParams();

      if (lat && lng) {
        params.append('lat', lat.toString());
        params.append('lng', lng.toString());
        params.append('radius', radius.toString());
      }

      if (searchLocation) {
        params.append('search', searchLocation);
      }

      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const page = resetPage ? 1 : currentPage;
      params.append('page', page.toString());
      params.append('limit', itemsPerPage.toString());

      if (resetPage) {
        setCurrentPage(1);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await apiCall(url);
      if (response.ok) {
        const data = await response.json();
        setStations(data.stations || data);
        setTotalStations(data.total || data.length || 0);
      }
    } catch (error) {
      console.error('Error loading stations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (userLocation) {
      loadStations(userLocation.lat, userLocation.lng, 10, true);
    } else {
      loadStations(null, null, null, true);
    }
  };

  const handleStationClick = (station) => {
    setSelectedStationId(station.id);
    setSelectedStation(station);
    setCurrentView('station');
  };

  const handleMapStationClick = (station) => {
    setSelectedStationId(station.id);
    // Don't navigate away in map view, just select the station
  };

  const handleViewDetails = (station) => {
    setSelectedStation(station);
    setCurrentView('station');
  };

  const handleDirections = (station) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lng}`;
    window.open(url, '_blank');
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalStations / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalStations);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Render Map View
  if (viewMode === 'map') {
    return (
      <div className="h-screen flex flex-col">
        {/* Map Controls Header */}
        <div className="bg-white border-b p-4 z-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search Controls */}
              <div className="flex gap-4 flex-1">
                <input
                  type="text"
                  placeholder="Search by location, station name..."
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="flex-1 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Search
                </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">View:</span>
                <div className="flex rounded border">
                  <button
                    onClick={() => setViewMode('cards')}
                    className="px-3 py-1 text-sm bg-white text-gray-700 hover:bg-gray-50 border-r"
                  >
                    🔲 Cards
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className="px-3 py-1 text-sm bg-white text-gray-700 hover:bg-gray-50 border-r"
                  >
                    📋 List
                  </button>
                </div>
              </div>

              {/* Quick Controls */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Radius:</label>
                  <select
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(Number(e.target.value))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={5}>5 km</option>
                    <option value={10}>10 km</option>
                    <option value={25}>25 km</option>
                    <option value={50}>50 km</option>
                  </select>
                </div>

                {userLocation && (
                  <button
                    onClick={() => {
                      setMapCenter([userLocation.lat, userLocation.lng]);
                      loadStations(userLocation.lat, userLocation.lng, searchRadius, true);
                      setSearchLocation('');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    📍 My Location
                  </button>
                )}
              </div>
            </div>

            {/* Results Info */}
            {totalStations > 0 && (
              <div className="mt-3 text-sm text-gray-600">
                Showing {stations.length} of {totalStations} stations
                {searchLocation && ` for "${searchLocation}"`}
              </div>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            className="z-10"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {userLocation && (
              <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
                <Popup>
                  <div className="text-center">
                    <p className="font-medium">Your Location</p>
                  </div>
                </Popup>
              </Marker>
            )}

            {stations.map((station) => (
              <Marker
                key={station.id}
                position={[station.lat, station.lng]}
                icon={createChargingIcon(station.max_power, selectedStationId === station.id)}
                eventHandlers={{
                  click: () => handleMapStationClick(station)
                }}
              >
                <Popup>
                  <StationPopup
                    station={station}
                    onViewDetails={handleViewDetails}
                    onDirections={handleDirections}
                  />
                </Popup>
              </Marker>
            ))}

            <MapController
              center={mapCenter}
              stations={stations}
              onStationClick={handleMapStationClick}
              selectedStationId={selectedStationId}
              userLocation={userLocation}
              searchRadius={searchRadius}
            />
          </MapContainer>

          {/* Map Legend */}
          <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 z-30">
            <h4 className="font-medium text-sm mb-2">Power Levels</h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                <span>Ultra Fast (150kW+)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                <span>Fast (50-149kW)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span>Rapid (22-49kW)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span>Standard (&lt;22kW)</span>
              </div>
            </div>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-25 flex items-center justify-center z-40">
              <div className="bg-white rounded-lg p-4">
                <div className="text-gray-600">Loading stations...</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular cards/list view (existing code)
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Search Section */}
      <div className="bg-white rounded border p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Find Charging Stations</h2>
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Search by location, station name..."
            value={searchLocation}
            onChange={(e) => setSearchLocation(e.target.value)}
            className="flex-1 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Search
          </button>
        </div>
        {userLocation && (
          <button
            onClick={() => loadStations(userLocation.lat, userLocation.lng, 10, true)}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            📍 Use my current location
          </button>
        )}
      </div>

      {/* Controls Section */}
      <div className="bg-white rounded border p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* View Mode Toggle - Updated with Map option */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">View:</span>
            <div className="flex rounded border">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1 text-sm ${
                  viewMode === 'cards'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                🔲 Cards
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-sm border-l ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                📋 List
              </button>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <div className="flex space-x-2">
              {[
                { key: 'name', label: 'Name' },
                { key: 'distance', label: 'Distance' },
                { key: 'power', label: 'Power' },
                { key: 'updated', label: 'Updated' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleSortChange(key)}
                  className={`px-3 py-1 text-sm rounded border ${
                    sortBy === key
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {label} {sortBy === key && (sortOrder === 'asc' ? '↑' : '↓')}
                </button>
              ))}
            </div>
          </div>

          {/* Items per page */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>
        </div>

        {/* Results Info */}
        <div className="mt-3 text-sm text-gray-600">
          {totalStations > 0 && (
            <span>
              Showing {startItem}-{endItem} of {totalStations} stations
              {searchLocation && ` for "${searchLocation}"`}
            </span>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="text-gray-600">Loading...</div>
        </div>
      )}

      {/* Stations Display */}
      {!loading && (
        <>
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stations.map((station) => (
                <StationCard
                  key={station.id}
                  station={station}
                  onClick={() => handleStationClick(station)}
                />
              ))}
            </div>
          ) : (
            <StationList
              stations={stations}
              onStationClick={handleStationClick}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </>
      )}

      {/* No Results */}
      {!loading && stations.length === 0 && (
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No stations found</h3>
          <p className="text-gray-500">Try adjusting your search criteria</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between bg-white border rounded p-4">
          <div className="text-sm text-gray-700 mb-4 sm:mb-0">
            Showing {startItem} to {endItem} of {totalStations} results
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' && handlePageChange(page)}
                disabled={page === '...'}
                className={`px-3 py-1 text-sm border rounded ${
                  page === currentPage
                    ? 'bg-blue-600 text-white border-blue-600'
                    : page === '...'
                    ? 'cursor-default'
                    : 'hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Your existing StationCard and StationList components remain the same
const StationCard = ({ station, onClick }) => {
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded border p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-lg text-gray-900">
          {station.name || 'Unnamed Station'}
        </h3>
        {station.max_power && (
          <span className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded">
            {station.max_power}kW
          </span>
        )}
      </div>

      <div className="flex items-center mb-3">
        {station.average_rating > 0 ? (
          <div className="flex items-center">
            <div className="flex">{renderStars(station.average_rating)}</div>
            <span className="text-sm text-gray-600 ml-2">
              ({station.average_rating.toFixed(1)})
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-500">No ratings yet</span>
        )}
      </div>

      <div className="text-sm text-gray-600 mb-4">
        <div className="flex items-center mb-2">
          <span>📍 {station.full_address || `${station.city}, ${station.country}`}</span>
        </div>

        {station.network && (
          <div className="text-blue-600">
            🔌 {station.network}
          </div>
        )}

        {station.distance_km && (
          <div className="text-gray-500 mt-1">
            📏 {station.distance_km.toFixed(1)} km away
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{station.comments?.length || 0} comments • {station.pictures?.length || 0} photos</span>
        <span className="text-blue-600 font-medium">View Details →</span>
      </div>
    </div>
  );
};

// StationList component (keeping existing functionality)
const StationList = ({ stations, onStationClick, sortBy, sortOrder, onSortChange }) => {
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  const SortableHeader = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => onSortChange(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortBy === field && (
          <span className="text-blue-600">
            {sortOrder === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded border overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <SortableHeader field="name">Station Name</SortableHeader>
            <th className="px-4 py-3 text-left">Location</th>
            <SortableHeader field="distance">Distance</SortableHeader>
            <SortableHeader field="power">Power</SortableHeader>
            <th className="px-4 py-3 text-left">Rating</th>
            <th className="px-4 py-3 text-left">Network</th>
            <SortableHeader field="updated">Last Updated</SortableHeader>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {stations.map((station, index) => (
            <tr
              key={station.id}
              className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}
            >
              <td className="px-4 py-4">
                <div className="font-medium text-gray-900">
                  {station.name || 'Unnamed Station'}
                </div>
                {station.operator_name && (
                  <div className="text-sm text-gray-500">
                    {station.operator_name}
                  </div>
                )}
              </td>

              <td className="px-4 py-4">
                <div className="text-sm text-gray-900">
                  {station.city}, {station.country}
                </div>
                {station.full_address && (
                  <div className="text-xs text-gray-500 truncate max-w-xs">
                    {station.full_address}
                  </div>
                )}
              </td>

              <td className="px-4 py-4 text-sm text-gray-900">
                {station.distance_km ? `${station.distance_km.toFixed(1)} km` : '-'}
              </td>

              <td className="px-4 py-4">
                {station.max_power ? (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    {station.max_power}kW
                  </span>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>

              <td className="px-4 py-4">
                {station.average_rating > 0 ? (
                  <div className="flex items-center">
                    <div className="flex text-sm">{renderStars(station.average_rating)}</div>
                    <span className="text-xs text-gray-600 ml-1">
                      ({station.average_rating.toFixed(1)})
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">No ratings</span>
                )}
              </td>

              <td className="px-4 py-4 text-sm">
                {station.network ? (
                  <span className="text-blue-600">{station.network}</span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </td>

              <td className="px-4 py-4 text-sm text-gray-500">
                {station.updated_at ?
                  new Date(station.updated_at).toLocaleDateString() :
                  new Date(station.created_at).toLocaleDateString()
                }
              </td>

              <td className="px-4 py-4">
                <button
                  onClick={() => onStationClick(station)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StationsView;